import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { CohortMultiSelect } from "@/components/CohortMultiSelect";
import { STATUS_META } from "@/lib/mockAdmin";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { listStudentApplications, withdrawAdmission, downloadApprovedApplicationsExcel } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { BulkStudentUploadDialog } from "@/components/BulkStudentUploadDialog";
import { Search, ChevronLeft, ChevronRight, Filter, Eye, RefreshCw, Loader2, XCircle, Upload, FileSpreadsheet } from "lucide-react";

const PAGE_SIZE = 10;

// Every registration_status this roster can show, in display order — mirrors
// the buckets used elsewhere (cohort stats, dashboard) plus the post-submit
// statuses a registration can actually reach.
const ROSTER_STATUS_FILTERS = ["not_started", "in_progress", "submitted", "verified", "on_hold", "rejected", "withdrawn"];

// Approved/Rejected/Withdrawn are "done" — sunk to the bottom so admins see
// applications that still need attention first. Array.sort is stable, so
// relative order within each bucket is otherwise unchanged.
const DONE_STATUSES = new Set(["verified", "rejected", "withdrawn"]);
const doneRank = (status) => (DONE_STATUSES.has(status) ? 1 : 0);

const initials = (name = "?") =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";

// The admissions roster (student_applications, read-only, admissions-owned) —
// every candidate with an offer letter, whether or not they've started this
// app's registration flow yet. Distinct from the "Registrations" tab, which
// is this app's own registration data (registrations table) — only
// candidates who've actually started show up there.
export default function AdminApplications() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cohortIds, setCohortIds] = useState([]);
  const { session } = useAdminAuth();
  const isAdmissions = session?.role === "admissions";
  const isRoadmin = session?.role === "roadmin" || session?.role === "superadmin";
  const isSuperadmin = session?.role === "superadmin";
  const showActions = true;
  const [withdrawFor, setWithdrawFor] = useState(null); // { id, name }
  const [withdrawReason, setWithdrawReason] = useState("");
  const [withdrawSaving, setWithdrawSaving] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);

  useEffect(() => {
    if (session?.role === "finance") nav("/admin/finance", { replace: true });
    if (session?.role === "checkin") nav("/admin/checkin", { replace: true });
  }, [session?.role, nav]);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    const params = cohortIds.length ? { cohort_ids: cohortIds.join(",") } : {};
    const data = await listStudentApplications(params);
    setRows(Array.isArray(data) ? data : []);
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortIds]);

  const applications = useMemo(() => rows.map((r) => ({
    id: r.application_number,
    applicationNumber: r.application_number,
    name: r.name || "—",
    email: r.email || "",
    mobile: r.mobile || "",
    rollNo: r.roll_no || "",
    programLabel: r.category_label || "—",
    status: r.registration_status || "not_started",
    hasRegistration: !!r.has_registration,
    withdrawalType: r.withdrawal_type || null,
    canAdmissionWithdraw: !!r.can_admission_withdraw,
  })), [rows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return applications
      .filter((a) => {
        const matchQ =
          !query ||
          (a.name || "").toLowerCase().includes(query) ||
          (a.applicationNumber || "").toLowerCase().includes(query) ||
          (a.email || "").toLowerCase().includes(query) ||
          (a.mobile || "").toLowerCase().includes(query) ||
          (a.rollNo || "").toLowerCase().includes(query);
        const matchS = status === "all" || a.status === status;
        return matchQ && matchS;
      })
      .sort((a, b) => doneRank(a.status) - doneRank(b.status));
  }, [applications, q, status]);

  // Admission-level withdrawal — for a candidate who hasn't been approved
  // yet (not started / in progress / submitted). Course-level withdrawal
  // (already approved) happens from the Registration detail page instead.
  const submitWithdraw = async () => {
    if (!withdrawFor) return;
    const reason = withdrawReason.trim();
    if (reason.length < 5) return toast.error("Please enter a reason (at least 5 characters)");
    setWithdrawSaving(true);
    const res = await withdrawAdmission(withdrawFor.id, { actor_role: session?.role, reason });
    setWithdrawSaving(false);
    if (!res.ok) {
      toast.error(res.data?.detail || "Could not withdraw — please try again");
      return;
    }
    toast.success(`${withdrawFor.name || withdrawFor.id} marked as withdrawn`);
    setWithdrawFor(null);
    setWithdrawReason("");
    load(true);
  };

  const handleApprovedExcel = async () => {
    setExcelDownloading(true);
    const res = await downloadApprovedApplicationsExcel(cohortIds);
    setExcelDownloading(false);
    if (!res.ok) {
      toast.error(res.data?.detail || "No approved applications to export");
      return;
    }
    toast.success("Downloading approved applications…");
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="min-h-screen flex bg-[#F7F7F7]">
      <DashboardSidebar variant="admin" />
      <main className="flex-1 p-6 lg:p-10 max-w-7xl w-full">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="tiny-label">Admin Console</div>
            <div className="flex items-center gap-3 flex-wrap mt-1">
              <h1 className="text-3xl md:text-4xl font-bold text-[#111] tracking-tight">Applications</h1>
              {isAdmissions && (
                <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-[#F3F4F6] text-[#6B7280]">
                  View only
                </span>
              )}
            </div>
            <p className="text-[#6B7280] mt-1">
              {isAdmissions
                ? "Every admitted candidate — whether or not they’ve started registering. Open View to preview uploaded documents. No changes can be made from this login."
                : "Every admitted candidate — whether or not they’ve started registering."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isRoadmin && (
              <button
                type="button"
                onClick={handleApprovedExcel}
                disabled={excelDownloading}
                className="mu-btn-secondary text-sm"
                data-testid="btn-download-approved-excel"
                title={
                  cohortIds.length
                    ? "Download approved applications for the selected cohorts as Excel"
                    : "Download all approved applications as Excel (name, application no., school, programme)"
                }
              >
                {excelDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                {excelDownloading ? "Preparing Excel…" : "Download approved (Excel)"}
              </button>
            )}
            {isSuperadmin && (
              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="mu-btn-primary text-sm"
                data-testid="btn-upload-student-applications"
              >
                <Upload className="h-4 w-4" /> Upload students
              </button>
            )}
            <button
              onClick={() => load()}
              className="mu-btn-secondary text-sm"
              data-testid="btn-refresh-student-applications"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </button>
          </div>
        </div>

        <div className="mu-card mt-8" data-testid="student-applications-table">
          <div className="p-4 md:p-5 border-b border-[#E5E7EB] flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="input-search-student-applications"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Search by name, roll no., application no., email or mobile"
                className="mu-input pl-9"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <CohortMultiSelect selected={cohortIds} onChange={(ids) => { setCohortIds(ids); setPage(1); }} />
              <Filter className="h-4 w-4 text-[#9CA3AF]" />
              <button
                onClick={() => { setStatus("all"); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  status === "all"
                    ? "bg-[#111] text-white border-[#111]"
                    : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#111]"
                }`}
                data-testid="filter-roster-status-all"
              >
                All
              </button>
              {ROSTER_STATUS_FILTERS.map((key) => {
                const meta = STATUS_META[key];
                if (!meta) return null;
                return (
                  <button
                    key={key}
                    onClick={() => { setStatus(key); setPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      status === key
                        ? "bg-[#111] text-white border-[#111]"
                        : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#111]"
                    }`}
                    data-testid={`filter-roster-status-${key}`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F7F7] text-[#6B7280] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Application No.</th>
                  <th className="text-left px-6 py-3 font-semibold">Candidate</th>
                  <th className="text-left px-6 py-3 font-semibold">Roll No.</th>
                  <th className="text-left px-6 py-3 font-semibold">Program</th>
                  <th className="text-left px-6 py-3 font-semibold">Registration Status</th>
                  {showActions && <th className="text-right px-6 py-3 font-semibold">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {loading ? (
                  <tr>
                    <td colSpan={showActions ? 6 : 5} className="px-6 py-16 text-center text-[#9CA3AF]">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading applications…
                    </td>
                  </tr>
                ) : paged.length === 0 ? (
                  <tr>
                    <td colSpan={showActions ? 6 : 5} className="px-6 py-14 text-center text-[#9CA3AF]">
                      No applications match your filters.
                    </td>
                  </tr>
                ) : (
                  paged.map((a) => (
                    <tr key={a.id} className="hover:bg-[#F7F7F7]/60 transition" data-testid={`student-app-row-${a.id}`}>
                      <td className="px-6 py-3 text-[#111] font-medium">{a.applicationNumber}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-[#F3F4F6] text-[#111] flex items-center justify-center text-xs font-bold shrink-0">
                            {initials(a.name)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-[#111] font-medium truncate">{a.name}</div>
                            <div className="text-xs text-[#6B7280] truncate">{a.email || a.mobile}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-[#6B7280]">{a.rollNo || "—"}</td>
                      <td className="px-6 py-3 text-[#6B7280]">{a.programLabel}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1.5">
                          <StatusBadge status={a.status} />
                          {a.withdrawalType && (
                            <span
                              className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold"
                              data-testid={`withdrawal-type-${a.id}`}
                            >
                              ({a.withdrawalType === "course" ? "Course" : "Admission"})
                            </span>
                          )}
                        </div>
                      </td>
                      {showActions && <td className="px-6 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          {a.hasRegistration ? (
                            <Link
                              to={`/admin/registrations/${encodeURIComponent(a.id)}`}
                              className="inline-flex items-center gap-1 text-[#B60F2A] hover:underline text-sm font-medium"
                              data-testid={`btn-view-registration-${a.id}`}
                            >
                              <Eye className="h-3.5 w-3.5" /> View
                            </Link>
                          ) : (
                            <span className="text-xs text-[#9CA3AF]">Not started</span>
                          )}
                          {isRoadmin && a.canAdmissionWithdraw && (
                            <button
                              type="button"
                              onClick={() => { setWithdrawFor({ id: a.id, name: a.name }); setWithdrawReason(""); }}
                              className="inline-flex items-center gap-1 text-red-700 hover:underline text-sm font-medium"
                              data-testid={`btn-admission-withdraw-${a.id}`}
                              title="Withdraw admission"
                            >
                              <XCircle className="h-3.5 w-3.5" /> Withdraw
                            </button>
                          )}
                        </div>
                      </td>}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t border-[#E5E7EB] flex items-center justify-between text-sm">
            <div className="text-[#6B7280]">
              Page <span className="font-medium text-[#111]">{currentPage}</span> of {totalPages} · {filtered.length} results
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="mu-btn-secondary py-2 px-3 disabled:opacity-40"
                data-testid="btn-prev-page-student-applications"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="mu-btn-secondary py-2 px-3 disabled:opacity-40"
                data-testid="btn-next-page-student-applications"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Admission-level withdrawal dialog */}
        <Dialog open={!!withdrawFor} onOpenChange={(open) => !open && setWithdrawFor(null)}>
          <DialogContent data-testid="admission-withdraw-dialog">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-700" />
                </div>
                <DialogTitle>Withdraw admission</DialogTitle>
              </div>
              <DialogDescription>
                This marks <span className="font-semibold text-[#111]">{withdrawFor?.name}</span>&rsquo;s admission as withdrawn — before they&rsquo;ve been approved. No finance clearance is required for this.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <label className="tiny-label" htmlFor="admission-withdraw-reason">Reason</label>
              <Textarea
                id="admission-withdraw-reason"
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                placeholder="e.g. Candidate informed us they've joined elsewhere."
                rows={4}
                className="mt-1"
                data-testid="admission-withdraw-reason-input"
              />
            </div>
            <DialogFooter className="mt-4">
              <button className="mu-btn-secondary" onClick={() => setWithdrawFor(null)} data-testid="btn-cancel-admission-withdraw">Cancel</button>
              <button
                className="mu-btn-primary bg-red-700 hover:bg-red-800"
                onClick={submitWithdraw}
                disabled={withdrawSaving}
                data-testid="btn-confirm-admission-withdraw"
              >
                {withdrawSaving ? "Withdrawing…" : (<><XCircle className="h-4 w-4" /> Withdraw admission</>)}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {isSuperadmin && (
          <BulkStudentUploadDialog
            open={uploadOpen}
            onOpenChange={setUploadOpen}
            onCommitted={() => load(true)}
          />
        )}
      </main>
    </div>
  );
}
